"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { BsGlobe2, BsSearch } from "react-icons/bs";
import { FiAlignCenter } from "react-icons/fi";

import { useTheme } from "@/components/providers/ThemeProvider";
import {
  HEADER_EPAPER_BADGE_CLASS,
  HEADER_LOGO_CLASS,
  HEADER_LOGO_HEIGHT,
  HEADER_LOGO_WIDTH,
  SITE_LOGO_BLACK_SRC,
  SITE_LOGO_WHITE_SRC,
  WEBSITE_NAME,
} from "@/data/constants";
import { SocialIconsKey, socialIconsMap } from "@/data/social";
import { StaticAdPageDataType } from "@/services/adServices";
import { ArticleCategoryType } from "@/types/article";
import { SocialLinkType } from "@/types/common";
import { format } from "date-fns";
import { enUS, te } from "date-fns/locale";
import { ensureHttps } from "@/utils/ensureHttps";
import { getLabel } from "@/data/labels";
import { getLocalePrefix } from "@/utils/getLocaleFromPath";
import AdBanner from "@/components/GoogleAdSense/AdBanner";
import HamburgerMenuModal from "./HamburgerMenuModal";
import HeaderMainNavigation from "./HeaderMainNavigation";
import SearchModal from "./SearchModal";

interface MainHeaderSectionProps {
  mainNavLinks: ArticleCategoryType[];
  categories: ArticleCategoryType[];
  socialLinks: SocialLinkType[];
  lang: string;
  advertisement: StaticAdPageDataType;
}

export default function MainHeaderSection({
  mainNavLinks,
  categories,
  socialLinks,
  lang,
  advertisement,
}: MainHeaderSectionProps) {
  const { darkMode } = useTheme();
  const pathname = usePathname();
  const localePrefix = getLocalePrefix(pathname);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const langCode = (lang?.split("-")[0] || "te") as AppLocale;
  const [hamburgerMenuOpen, setHamburgerMenuOpen] = useState(false);
  const advertisementData = advertisement?.data?.adBlocks?.find(item => {
    return item?.adName === "topHeaderAd";
  });

  return (
    <>
      {advertisementData && (
        <div className="bg-white dark:bg-darkBgPrimaryColor lg:hidden">
          <div className="container py-1">
            <div className="site-header-ad site-header-ad--mobile">
              <AdBanner
                advertisementData={advertisementData}
                variant="compact"
                compactLayout="strip"
              />
            </div>
          </div>
        </div>
      )}

      <div className="sticky top-0 z-20 transition-all duration-500 bg-white dark:bg-darkBgPrimaryColor">
        <div className="container">
          <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_2.5rem] items-center gap-2 py-3 min-h-[52px] lg:min-h-0 lg:grid-cols-[minmax(0,auto)_minmax(0,1fr)_minmax(0,auto)] lg:items-center lg:gap-3 lg:py-2.5 xl:gap-4">
            <button
              type="button"
              onClick={() => setHamburgerMenuOpen(true)}
              className="col-start-1 row-start-1 flex h-10 w-10 items-center justify-center rounded text-gray-900 transition-colors hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800 lg:hidden"
              aria-label="Open menu"
            >
              <FiAlignCenter size={24} className="h-6 w-6" />
            </button>

            <button
              type="button"
              onClick={() => setSearchModalOpen(true)}
              className="col-start-3 row-start-1 flex h-10 w-10 items-center justify-center justify-self-end rounded text-gray-900 transition-colors hover:bg-gray-100 dark:text-white dark:hover:bg-gray-800 lg:hidden"
              aria-label="Search"
            >
              <BsSearch size={22} className="h-5 w-5" />
            </button>

            <div className="col-start-2 row-start-1 flex min-w-0 flex-col items-center gap-1 md:pb-3 lg:col-start-1 lg:row-start-1 lg:max-w-[min(100%,380px)] lg:gap-1.5">
              <Link
                href="/"
                aria-label="Go to home page"
                className="flex items-center justify-center"
              >
                <img
                  className={HEADER_LOGO_CLASS}
                  alt={WEBSITE_NAME}
                  src={!darkMode ? SITE_LOGO_BLACK_SRC : SITE_LOGO_WHITE_SRC}
                  width={HEADER_LOGO_WIDTH}
                  height={HEADER_LOGO_HEIGHT}
                  fetchPriority="high"
                  decoding="sync"
                />
              </Link>
              <span className="mt-2 w-full text-center text-[11px] font-[500] uppercase tracking-wide text-[#AEAEAE] whitespace-nowrap sm:text-xs lg:mt-2.5">
                {format(new Date(), "EEEE, dd MMMM yyyy", {
                  locale: langCode === "te" ? te : enUS,
                })}
              </span>
              <Link
                href={`${localePrefix || "/en-IN"}/epaper`}
                className={`${HEADER_EPAPER_BADGE_CLASS} hover:opacity-90 transition-opacity lg:hidden`}
                aria-label="View E-Paper"
              >
                {getLabel(langCode, "E_PAPER")}
              </Link>
            </div>

            {advertisementData && (
              <div className="hidden min-w-0 items-center justify-center self-stretch px-1 lg:col-start-2 lg:flex xl:px-2">
                <div className="site-header-ad site-header-ad--inline w-full min-w-0 max-w-full">
                  <AdBanner
                    advertisementData={advertisementData}
                    variant="compact"
                    compactLayout="inline"
                  />
                </div>
              </div>
            )}

            {/* Right: E-PAPER above social row (desktop) */}
            <div className="hidden shrink-0 items-center justify-end lg:col-start-3 lg:flex">
              <div className="flex flex-col items-end gap-2 shrink-0">
                <Link
                  href={`${localePrefix || "/en-IN"}/epaper`}
                  className={`${HEADER_EPAPER_BADGE_CLASS} hover:opacity-90 transition-opacity`}
                  aria-label="View E-Paper"
                >
                  {getLabel(langCode, "E_PAPER")}
                </Link>
                <div className="flex items-center gap-2.5 flex-nowrap">
                  {socialLinks?.map((socialLink, index) => {
                    const Icon =
                      socialIconsMap[socialLink?.platform as SocialIconsKey] || BsGlobe2;
                    return (
                      <div key={index} className="flex items-center">
                        <p className="w-8 h-8 rounded-full shrink-0 bg-[#F9F9F9] dark:bg-[#3b3b3b] dark:text-white border flex justify-center items-center">
                          <a
                            href={ensureHttps(socialLink?.url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Follow us on ${socialLink?.platform}`}
                          >
                            <Icon />
                          </a>
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigations */}
      <HeaderMainNavigation
        searchModalOpen={searchModalOpen}
        setSearchModalOpen={setSearchModalOpen}
        setHamburgerMenuOpen={setHamburgerMenuOpen}
        mainNavLinks={mainNavLinks || []}
        categories={categories || []}
        socialLinks={socialLinks || []}
      />

      {/* Search Modal */}
      {searchModalOpen && (
        <SearchModal setSearchModalOpen={setSearchModalOpen} socialLinks={socialLinks || []} />
      )}

      {hamburgerMenuOpen && (
        <HamburgerMenuModal
          setHamburgerMenuOpen={setHamburgerMenuOpen}
          categories={categories || []}
          socialLinks={socialLinks || []}
        />
      )}
    </>
  );
}